import { apiBaseUrl } from "@/lib/utils/env";
import { useSession } from "@/lib/store/session";
import { nativeFetch, usesNativeHttp } from "@/lib/native/http";

export class ApiError extends Error {
  status: number;
  payload: unknown;
  constructor(message: string, status: number, payload?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.payload = payload;
  }
}

export type ApiOptions = {
  method?: "GET" | "POST" | "DELETE" | "PUT";
  body?: unknown;
  /** Send a specific key instead of the session's (Garmin temp guid). */
  authKeyOverride?: string;
  /** Don't log the session out on 401 (public activity views). */
  ignoreAuthFailure?: boolean;
  signal?: AbortSignal;
};

export function apiUrl(path: string): string {
  return apiBaseUrl() + path.replace(/^\/+/, "");
}

export function authHeaders(override?: string): Record<string, string> {
  const { userId, impersonateId } = useSession.getState();
  const key = override ?? userId;
  const headers: Record<string, string> = { "x-lucidlogicversion": "2" };
  if (key && key.length > 10) headers["Authorization"] = "key " + key;
  if (impersonateId) headers["impersonate-id"] = impersonateId;
  return headers;
}

type Envelope = {
  status?: string;
  error?: boolean;
  message?: unknown;
  detail?: unknown;
  /** "session_invalid" marks the one 401 that means the credential is dead (#289). */
  code?: string;
};

function isFailure(res: Response, json: Envelope | null): boolean {
  if (!res.ok) return true;
  if (!json || typeof json !== "object") return false;
  return json.error === true || json.status === "error";
}

function messageOf(res: Response, json: Envelope | null): string {
  const m = json && (typeof json.message === "string" ? json.message : undefined);
  const d = json && (typeof json.detail === "string" ? json.detail : undefined);
  return m || d || res.statusText || `Request failed (${res.status})`;
}

type ApiInit = {
  method: string;
  headers: Record<string, string>;
  body?: string;
  credentials: RequestCredentials;
  signal?: AbortSignal;
  redirect?: RequestRedirect;
};

/**
 * `fetch`, except in the iOS shell, where API Gateway's CORS cannot admit the
 * WebView's custom-scheme origin and the request goes through the native
 * layer instead (#379, lib/native/http.ts). The native path never follows a
 * redirect itself; apiRedirectJson does that by hand.
 */
function apiFetch(url: string, init: ApiInit): Promise<Response> {
  if (usesNativeHttp()) {
    return nativeFetch(url, { method: init.method, headers: init.headers, body: init.body });
  }
  return fetch(url, init);
}

/**
 * Typed fetch against pbranks-server. Normalises the server's inconsistent
 * envelopes ({status:"error"} / {error:true} / non-2xx) into ApiError.
 */
export async function api<T>(path: string, opts: ApiOptions = {}): Promise<T> {
  const method = opts.method ?? "POST";
  const res = await apiFetch(apiUrl(path), {
    method,
    headers: {
      "Content-Type": "application/json",
      ...authHeaders(opts.authKeyOverride),
    },
    body: method === "GET" ? undefined : JSON.stringify(opts.body ?? {}),
    signal: opts.signal,
    credentials: "include",
  });

  let json: (Envelope & Record<string, unknown>) | null = null;
  const text = await res.text();
  if (text) {
    try {
      json = JSON.parse(text);
    } catch {
      json = null;
    }
  }

  if (isFailure(res, json)) {
    // Sign out ONLY when the server says the credential itself was rejected
    // (#289). Clearing on every 401 meant a wrong email code, a failed
    // account deletion, or one stray 401 on the background poll that fires
    // whenever iOS foregrounds the app all ended the session silently.
    const credentialRejected = res.status === 401 && json?.code === "session_invalid";
    if (credentialRejected && !opts.ignoreAuthFailure && !opts.authKeyOverride) {
      useSession.getState().logout();
    }
    throw new ApiError(messageOf(res, json), res.status, json ?? text);
  }
  return json as T;
}

/** Follow the presigned-S3 redirect the server issues and return the JSON body. */
export async function apiRedirectJson<T>(path: string, body: unknown, opts: ApiOptions = {}): Promise<T> {
  const url = apiUrl(path);
  let res = await apiFetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders(opts.authKeyOverride) },
    body: JSON.stringify(body ?? {}),
    redirect: "follow",
    signal: opts.signal,
    // Deliberately NOT "include" (#290): this follows a 302 to a presigned S3
    // URL, and S3 cannot send Access-Control-Allow-Credentials at all, so a
    // credentialed request could never read the response. It authenticates
    // with the Authorization header instead, and the cookie would be useless
    // on the S3 hop regardless.
    credentials: "omit",
  });
  if (usesNativeHttp() && res.status >= 300 && res.status < 400) {
    // Second hop by hand (#379): a native redirect would resend the request
    // headers, and S3 rejects a presigned URL that also carries Authorization.
    // Resolved against the absolute API URL - in a live-reload build `url` is
    // the dev proxy's relative /api path.
    const location = res.headers.get("location");
    if (!location) throw new ApiError("Redirect without a Location header", res.status);
    const target = new URL(location, new URL(url, window.location.href)).toString();
    res = await nativeFetch(target, { method: "GET", followRedirects: true });
  }
  if (!res.ok) {
    let json: Envelope | null = null;
    try {
      json = await res.json();
    } catch {
      /* ignore */
    }
    throw new ApiError(messageOf(res, json), res.status, json);
  }
  return (await res.json()) as T;
}
